import { useState } from 'react'
import type { ChessMode, ChessDifficulty } from './chessData'
import RecognizeMode from './modes/RecognizeMode'
import MovementMode from './modes/MovementMode'
import CaptureMode from './modes/CaptureMode'
import PuzzleMode from './modes/PuzzleMode'
import { useTranslation } from '../../i18n/LanguageContext'
import { useSessionMode } from '../../professional/SessionModeContext'
import GameSetupScreen from '../GameSetupScreen'
import GameCompleteScreen from '../GameCompleteScreen'
import { formatGameTime } from '../utils'
import { ss } from '../gameSetupStyles'

interface Props {
  onBack: () => void
}

type GameScreen = 'setup' | 'playing' | 'complete'

const MODES: ChessMode[]       = ['recognize', 'movement', 'capture', 'puzzle']
const DIFFICULTIES: ChessDifficulty[] = ['easy', 'medium', 'hard']

const MODE_EMOJIS: Record<ChessMode, string> = {
  recognize: '♟',
  movement:  '♞',
  capture:   '♜',
  puzzle:    '♛',
}

interface GameStats {
  moves:        number
  startTime:    number | null
  endTime:      number | null
  pairsTotal:   number
  pairsMatched: number
}

export default function ChessGame({ onBack }: Props) {
  const { t }       = useTranslation()
  const ch          = t.chess
  const sessionMode = useSessionMode()

  const [screen,     setScreen]     = useState<GameScreen>(sessionMode ? 'playing' : 'setup')
  const [mode,       setMode]       = useState<ChessMode>((sessionMode?.config.mode as ChessMode) ?? 'recognize')
  const [difficulty, setDifficulty] = useState<ChessDifficulty>((sessionMode?.config.difficulty as ChessDifficulty) ?? 'easy')
  const [lastStats,  setLastStats]  = useState<GameStats | null>(null)
  const [gameKey,    setGameKey]    = useState(0) // increment to force mode component remount on new game

  const handleCompleted = (stats: GameStats) => {
    setLastStats(stats)
    setScreen('complete')
  }

  const handlePlayAgain = () => {
    setGameKey((k) => k + 1)
    setScreen('playing')
  }

  const handleChangeLevel = () => {
    setScreen('setup')
  }

  const handleStart = () => {
    setGameKey((k) => k + 1)
    setScreen('playing')
  }

  return (
    <div style={{ minHeight: '100vh' }}>
      {screen === 'setup' && (
        <SetupScreen
          mode={mode}
          difficulty={difficulty}
          onModeChange={setMode}
          onDifficultyChange={setDifficulty}
          onStart={handleStart}
          onBack={onBack}
        />
      )}

      {screen === 'playing' && (
        <PlayingScreen
          key={gameKey}
          mode={mode}
          difficulty={difficulty}
          onCompleted={handleCompleted}
          onBack={sessionMode ? onBack : () => setScreen('setup')}
        />
      )}

      {screen === 'complete' && lastStats && (
        <GameCompleteScreen
          emoji="♟"
          title={ch.complete.title}
          subtitle={ch.complete.subtitle}
          stats={[
            { value: String(lastStats.moves),                                   label: ch.complete.movesLabel },
            { value: formatGameTime(lastStats.startTime, lastStats.endTime),    label: ch.complete.timeLabel },
            { value: ch.difficulty[difficulty].label,                           label: ch.complete.levelLabel },
          ]}
          playAgainLabel={ch.complete.playAgain}
          changeLevelLabel={ch.complete.changeLevel}
          backLabel={ch.complete.backToDashboard}
          onPlayAgain={handlePlayAgain}
          onChangeLevel={handleChangeLevel}
          onBack={onBack}
        />
      )}
    </div>
  )
}

/* ── Setup Screen ───────────────────────────────────────────── */
function SetupScreen({ mode, difficulty, onModeChange, onDifficultyChange, onStart, onBack }: {
  mode: ChessMode; difficulty: ChessDifficulty
  onModeChange: (m: ChessMode) => void; onDifficultyChange: (d: ChessDifficulty) => void
  onStart: () => void; onBack: () => void
}) {
  const { t } = useTranslation()
  const ch    = t.chess

  return (
    <GameSetupScreen
      title={ch.title}
      subtitle={ch.subtitle}
      backLabel={ch.backToDashboard}
      onBack={onBack}
      difficulties={DIFFICULTIES.map((id) => ({ id, ...ch.difficulty[id] }))}
      selectedDifficulty={difficulty}
      onDifficultyChange={(id) => onDifficultyChange(id as ChessDifficulty)}
      chooseDifficultyLabel={ch.chooseDifficultyTitle}
      startLabel={ch.startBtn}
      onStart={onStart}
    >
      {/* Mode selector */}
      <section className="fade-up" style={{ animationDelay: '0.1s' }}>
        <h2 style={ss.optionLabel}>{ch.chooseModeTitle}</h2>
        <div style={ss.modeGrid}>
          {MODES.map((id) => (
            <button
              key={id}
              onClick={() => onModeChange(id)}
              style={{ ...ss.modeCard, ...(mode === id ? ss.modeCardActive : {}) }}
              aria-pressed={mode === id}
            >
              <span style={{ fontSize: '1.75rem', lineHeight: 1 }}>{MODE_EMOJIS[id]}</span>
              <span style={{ fontSize: '1rem', fontWeight: 700, color: '#2E1F14' }}>{ch.modes[id].label}</span>
              <span style={{ fontSize: '0.875rem', color: '#6B4C3B', fontWeight: 500, lineHeight: 1.4 }}>{ch.modes[id].description}</span>
            </button>
          ))}
        </div>
      </section>
    </GameSetupScreen>
  )
}

/* ── Playing Screen dispatcher ───────────────────────────────── */
function PlayingScreen({ mode, difficulty, onCompleted, onBack }: {
  mode: ChessMode; difficulty: ChessDifficulty
  onCompleted: (stats: GameStats) => void; onBack: () => void
}) {
  const props = { difficulty, onCompleted, onBack }
  switch (mode) {
    case 'recognize': return <RecognizeMode {...props} />
    case 'movement':  return <MovementMode  {...props} />
    case 'capture':   return <CaptureMode   {...props} />
    case 'puzzle':    return <PuzzleMode    {...props} />
  }
}
